import { getRoundedDownValue } from "@/utils/helpers";
import { add } from "@/utils/math";

import { CURRENCIES } from "@/constants/currencies";

const USDT_CURRENCIES = ["USDT_OMNI", "USDT_ERC20", "USDT_TRC20"];
const USDT_CURRENCIES_SET = new Set(USDT_CURRENCIES);

function mapXBTToBTC(item) {
  if (item.currency === "XBT") {
    return {
      ...item,
      currency: "BTC",
    };
  }
  return item;
}

function getInstrumentsByCurrency(currency) {
  const store = require("@/store/store");
  const quoteCurrencyToInstrumentsMap =
    store.default.getters["config/quoteCurrencyToInstrumentsMap"];

  if (!currency) {
    return [];
  }

  return quoteCurrencyToInstrumentsMap[currency] || [];
}

function getEmptySpot(apiKeyId, currency, exchangeId) {
  const instruments = getInstrumentsByCurrency(currency);

  return instruments.map((instrument) => {
    return {
      exchangeId,
      apiKeyId,
      accountType: `${instrument.contractType}-${instrument.contract}`,
      currency: instrument.quoteCurrency,
      balance: 0,
    };
  });
}

function getBitfinexMarginApiKeys(currency) {
  const store = require("@/store/store");
  const state = store.default.state;
  const getters = store.default.getters;
  const { exchangeNames, getExchangeFullName, derivativesCollateralMap } =
    getters;
  const getApiKey = getters["portfolio/getApiKey"];

  function checkAccount(item, type) {
    const key = `${item.id}:${item.currency}:${type}`;
    return derivativesCollateralMap[key];
  }

  return state.portfolio.activePortfolioApiKeys.reduce((memo, item) => {
    const existing = checkAccount(item, "margin-cross");

    const condition =
      item.exchangeId === exchangeNames.BITFINEX &&
      item?.restrictions?.margin_enabled &&
      !existing;

    if (condition) {
      const exchangeFullName = getExchangeFullName(item.exchangeId);
      const { name: apiKeyName } = getApiKey(item.id) ?? {};

      memo.push({
        exchangeId: item.exchangeId,
        apiKeyId: item.id,
        accountType: "margin-cross",
        apiKeyName,
        currency,
        total: 0,
        exchangeFullName,
      });
    }

    return memo;
  }, []);
}

function getBitfinexPerpetualApiKeys(currency) {
  const store = require("@/store/store");
  const state = store.default.state;
  const getters = store.default.getters;
  const getApiKey = getters["portfolio/getApiKey"];
  const getExchangeFullName = getters.getExchangeFullName;
  const exchangeNames = getters.exchangeNames;
  const derivativesCollateralMap = getters.derivativesCollateralMap;

  function checkAccount(item, currency, type) {
    const key = `${item.id}:${currency}:${type}`;
    return derivativesCollateralMap[key];
  }

  const result = state.portfolio.activePortfolioApiKeys.reduce((memo, item) => {
    const existing = checkAccount(item, "perpetual");
    const condition =
      item.exchangeId === exchangeNames.BITFINEX &&
      item?.restrictions?.perpetuals_enabled &&
      !existing;

    if (condition) {
      const exchangeFullName = getExchangeFullName(item.exchangeId);
      const { name: apiKeyName } = getApiKey(item.id) || {};

      memo.push({
        exchangeId: item.exchangeId,
        apiKeyId: item.id,
        accountType: "perpetual",
        apiKeyName,
        currency,
        total: 0,
        exchangeFullName,
      });
    }

    return memo;
  }, []);

  return result;
}

function getGateEmptyApiKeys(currency) {
  const store = require("@/store/store");
  const state = store.default.state;
  const getters = store.default.getters;
  const getApiKey = getters["portfolio/getApiKey"];
  const { activePortfolioApiKeys } = state.portfolio;
  const getExchangeFullName = getters.getExchangeFullName;
  const exchangeNames = getters.exchangeNames;
  const gateUnifiedAccountsSet = new Set(state.apiKeys.gateUnifiedAccounts);
  const derivativesCollateralMap = getters.derivativesCollateralMap;

  function hasAccount(item, accountCurrency, accountType) {
    return Boolean(
      derivativesCollateralMap[`${item.id}:${accountCurrency}:${accountType}`],
    );
  }

  function addEmptyAccount(memo, item, accountCurrency, accountType) {
    if (hasAccount(item, accountCurrency, accountType)) {
      return;
    }

    const exchangeFullName = getExchangeFullName(item.exchangeId);
    const { name: apiKeyName } = getApiKey(item.id) || {};

    memo.push({
      exchangeId: item.exchangeId,
      apiKeyId: item.id,
      apiKeyName,
      currency: accountCurrency,
      accountType,
      total: 0,
      exchangeFullName,
    });
  }

  const result = [];

  activePortfolioApiKeys
    .filter(({ exchangeId }) => exchangeId === exchangeNames.GATE)
    .forEach((item) => {
      const isUnified = gateUnifiedAccountsSet.has(item.id);

      if (isUnified) {
        addEmptyAccount(result, item, currency, "unified-account");
      }

      addEmptyAccount(result, item, "USDT", "future-usdt-m");

      if (!isUnified) {
        addEmptyAccount(result, item, "USDT", "perpetual-usdt-m");
      }
      addEmptyAccount(result, item, "USD1", "perpetual-usdt-m");
      addEmptyAccount(result, item, "BTC", "perpetual-coin-m");

      if (!isUnified) {
        addEmptyAccount(result, item, "USDT", "option");
      }
    });

  return result;
}

function getEmptyCollateral(apiKeyId, currency, exchangeId) {
  const store = require("@/store/store");
  const derivativesAccountsByApiKeyIdMap = store.default.getters;

  const accounts = derivativesAccountsByApiKeyIdMap[apiKeyId] ?? [];
  const contractsSet = new Set(accounts.map(({ accountType }) => accountType));
  const instruments = getInstrumentsByCurrency(currency);
  const emptyInstruments = instruments.filter(
    ({ contractType, contract }) =>
      !contractsSet.has(`${contractType}-${contract}`),
  );

  return emptyInstruments.map((instrument) => {
    return {
      exchangeId,
      apiKeyId,
      accountType: `${instrument.contractType}-${instrument.contract}`,
      currency: instrument.quoteCurrency,
      balance: 0,
    };
  });
}

function getBitmexApiKeys(currency, collateral) {
  const store = require("@/store/store");
  const state = store.default.state;
  const getters = store.default.getters;
  const getApiKey = getters["portfolio/getApiKey"];
  const getExchangeFullName = getters.getExchangeFullName;
  const exchangeNames = getters.exchangeNames;

  const collateralMap = collateral.reduce((memo, item) => {
    const key = `${item.apiKeyId}:${exchangeNames.BITMEX}:${item.currency}`;
    memo[key] = item;
    return memo;
  }, {});

  function checkAccount(item) {
    const key = `${item.id}:${exchangeNames.BITMEX}:${item.currency}`;
    return collateralMap[key];
  }

  const currenciesSet = new Set(["USDT", "BTC", "APE", "MATIC"]);

  return state.portfolio.activePortfolioApiKeys.reduce((memo, item) => {
    const isExisting = checkAccount(item);

    const condition =
      item.exchangeId === "bitmex" &&
      currenciesSet.has(currency) &&
      !isExisting;

    if (condition) {
      const exchangeFullName = getExchangeFullName(item.exchangeId);
      const { name: apiKeyName } = getApiKey(item.id) ?? {};

      memo.push({
        exchangeId: item.exchangeId,
        apiKeyId: item.id,
        accountType: "margin",
        apiKeyName,
        currency,
        total: 0,
        exchangeFullName,
      });
    }

    return memo;
  }, []);
}

function getExistingAccountOptionsSet(existingOptions = []) {
  return new Set(
    existingOptions.map(({ apiKeyId, currency, accountType }) => {
      return `${apiKeyId}:${currency}:${accountType}`;
    }),
  );
}

const BINANCE_MARGIN_ACCOUNT_PATTERN =
  /^(?!portfolio margin$)[a-z0-9]+ margin$/i;

export function isBinanceMarginAccount(item) {
  const store = require("@/store/store");
  const { exchangeNames } = store.default.getters;

  return (
    item?.exchangeId === exchangeNames.BINANCE &&
    [item.accountType, item.formattedAccountType].some((accountType) =>
      BINANCE_MARGIN_ACCOUNT_PATTERN.test(accountType || ""),
    )
  );
}

export function isBinancePm1Account(item) {
  const store = require("@/store/store");
  const { derivativesCollateral } = store.default.state;
  const { exchangeNames } = store.default.getters;

  return (
    item?.exchangeId === exchangeNames.BINANCE &&
    (item.marginMode === "PM_1" ||
      derivativesCollateral.some(
        (collateral) =>
          collateral.exchangeId === exchangeNames.BINANCE &&
          collateral.apiKeyId === item.apiKeyId &&
          collateral.marginMode === "PM_1",
      ))
  );
}

export function isUnavailableBinanceTransferAccount(item, selectedAccount) {
  return (
    isBinanceMarginAccount(item) &&
    (isBinancePm1Account(item) || isBinancePm1Account(selectedAccount))
  );
}

function handleUnifiedAccounts(accountOptions, currency) {
  const store = require("@/store/store");
  const getters = store.default.getters;
  const getApiKey = getters["portfolio/getApiKey"];
  const getExchangeFullName = getters.getExchangeFullName;
  const exchangeNames = getters.exchangeNames;
  const derivativesCollateralMap = getters.derivativesCollateralMap;
  const aggregatedParamsApiKeyIdAccountTypeMap =
    getters.aggregatedParamsApiKeyIdAccountTypeMap;
  const apiKeys = store.default.state.portfolio.activePortfolio?.ApiKeys;
  const existingOptionsSet = getExistingAccountOptionsSet(accountOptions);

  const filteredApiKeys = apiKeys.filter(({ exchangeId }) =>
    [
      exchangeNames.HYPERLIQUID,
      exchangeNames.OKEX_V5,
      exchangeNames.OKEX_EU,
      exchangeNames.BYBIT,
      exchangeNames.BITGET,
      exchangeNames.COPPER_BITGET,
      exchangeNames.KUCOIN_HF,
    ].includes(exchangeId),
  );

  const emptyUnifiedAccounts = filteredApiKeys.reduce((memo, item) => {
    const { id, exchangeId } = item;
    const derivativesAccountKey = `${id}:${currency}:unified-account`;
    const aggregatedParamsKey = `${id}:unified-account`;

    if (derivativesCollateralMap[derivativesAccountKey]) {
      return memo;
    }

    if (existingOptionsSet.has(derivativesAccountKey)) {
      return memo;
    }

    if (!aggregatedParamsApiKeyIdAccountTypeMap[aggregatedParamsKey]?.length) {
      return memo;
    }

    const exchangeFullName = getExchangeFullName(exchangeId);
    const { name: apiKeyName } = getApiKey(id) ?? {};

    memo.push({
      exchangeId,
      exchangeFullName,
      apiKeyId: id,
      accountType: "unified-account",
      apiKeyName,
      currency,
      total: 0,
    });

    return memo;
  }, []);

  return accountOptions.concat(emptyUnifiedAccounts);
}

function handleBinanceMarginProAccounts(accountOptions, currency) {
  const store = require("@/store/store");
  const getters = store.default.getters;
  const getApiKey = getters["portfolio/getApiKey"];
  const getExchangeFullName = getters.getExchangeFullName;
  const exchangeNames = getters.exchangeNames;
  const derivativesCollateralMap = getters.derivativesCollateralMap;
  const aggregatedParamsApiKeyIdAccountTypeMap =
    getters.aggregatedParamsApiKeyIdAccountTypeMap;
  const apiKeys = store.default.state.portfolio.activePortfolio?.ApiKeys;
  const accountTypes = [
    "portfolio_margin",
    "portfolio_margin_pro",
    "portfolio_margin_pro_span",
  ];
  const existingOptionsSet = getExistingAccountOptionsSet(accountOptions);

  const filteredApiKeys = apiKeys.filter(
    ({ exchangeId }) => exchangeId === exchangeNames.BINANCE,
  );

  const emptyBinanceMarginProAccounts = filteredApiKeys.reduce((memo, item) => {
    const { id, exchangeId } = item;
    const exchangeFullName = getExchangeFullName(exchangeId);
    const { name: apiKeyName } = getApiKey(id) ?? {};

    accountTypes.forEach((accountType) => {
      const derivativesAccountKey = `${id}:${currency}:${accountType}`;
      const aggregatedParamsKey = `${id}:${accountType}`;

      if (derivativesCollateralMap[derivativesAccountKey]) {
        return;
      }

      if (existingOptionsSet.has(derivativesAccountKey)) {
        return;
      }

      if (
        !aggregatedParamsApiKeyIdAccountTypeMap[aggregatedParamsKey]?.length
      ) {
        return;
      }

      memo.push({
        exchangeId,
        exchangeFullName,
        apiKeyId: id,
        accountType,
        apiKeyName,
        currency,
        total: 0,
      });
    });

    return memo;
  }, []);

  return accountOptions.concat(emptyBinanceMarginProAccounts);
}

function getMarginEmptyAccounts(accountOptions, currency, exchange) {
  const store = require("@/store/store");

  const { derivativesCollateral } = store.default.state;

  let result = [];

  const accounts = accountOptions
    .concat(derivativesCollateral)
    .filter((item) => item.exchangeId === exchange);

  const mappedAccounts = [
    ...new Map(accounts.map((item) => [item.apiKeyId, item])).values(),
  ];

  for (const account of mappedAccounts) {
    if (account.accountType === "spot") {
      result = result.concat(
        getEmptySpot(account.apiKeyId, currency, exchange),
      );
    } else {
      result = result.concat(
        getEmptyCollateral(account.apiKeyId, currency, exchange),
      );
    }
  }

  const apiKeys = store.default.state.portfolio.activePortfolio?.ApiKeys;

  const keyToApiKeyMap = apiKeys?.reduce((memo, item) => {
    const { exchangeId, id } = item;

    const key = `${exchangeId}:${id}`;

    memo[key] = item;

    return memo;
  }, {});

  return result.filter((item) => {
    const { exchangeId, id } = item;

    const key = `${exchangeId}:${id}`;
    const restrictions = keyToApiKeyMap[key]?.restrictions;

    if (item.accountType.includes("margin-isolated")) {
      return restrictions?.margin_enabled;
    }

    if (item.accountType.includes("perpetual")) {
      return restrictions?.perpetuals_enabled;
    }

    if (item.accountType.includes("cash_settled")) {
      return restrictions?.futures_enabled;
    }

    return false;
  });
}

export function showRawMarginAccountType(option) {
  const store = require("@/store/store");
  const { exchangeNames } = store.default.getters;

  return (
    option.accountType.includes("margin-isolated") &&
    (option.exchangeId === exchangeNames.HITBTC ||
      option.exchangeId === exchangeNames.BEQUANT)
  );
}

export function showRawPerpetualAccountType(option) {
  const store = require("@/store/store");
  const { exchangeNames } = store.default.getters;

  return (
    option.accountType.includes("perpetual-") &&
    (option.exchangeId === exchangeNames.HITBTC ||
      option.exchangeId === exchangeNames.BEQUANT ||
      option.exchangeId === exchangeNames.BITFINEX)
  );
}

const searchBy1 = ["exchangeFullName", "apiKeyName", "exchangeId"];
const searchBy2 = ["formattedAccountType", "exchangeId", "exchangeFullName"];
const searchBy3 = [
  "accountType",
  "formattedAccountType",
  "exchangeId",
  "exchangeFullName",
];

export function getGroupedOptions(options, currency) {
  const store = require("@/store/store");
  const { exchangeNames } = store.default.getters;

  const grouped = {};

  for (const option of options) {
    const uid = `${option.apiKeyId}-${option.accountType}`;
    const item = {
      ...option,
      uid,
      searchBy: searchBy1,
    };

    if (!grouped[item.apiKeyId]) {
      grouped[item.apiKeyId] = {
        ...item,
        options: [],
        total: "0",
      };
    }

    grouped[item.apiKeyId].options.push(item);
  }

  const result = Object.values(grouped).map((item) => {
    item.total = item.options.reduce((memo, current) => {
      const total = current.total ? +current.total : 0;
      return add(memo, Math.max(total, 0));
    }, "0");

    item.total = getRoundedDownValue(item.total, item.currency);
    if (item.options.length > 1) {
      item.options = item.options
        .map((option) => processNestedOption(option, exchangeNames))
        .filter(
          (filteredOption) =>
            !(
              filteredOption.exchangeId === exchangeNames.HUOBI &&
              (filteredOption.formattedAccountType === "perpetuals" ||
                filteredOption.accountType === "future") &&
              currency === CURRENCIES.USDT
            ),
        );
    } else {
      delete item.options;
    }

    return item;
  });

  return result;
}

// Helper to process nested options
function processNestedOption(option, exchangeNames) {
  option.nested = true;
  option.groupBy = true;
  option.searchBy = searchBy2;
  option.groupKey = "formattedAccountType";
  option.formattedAccountType = option.accountType;

  if (showRawPerpetualAccountType(option)) {
    option.formattedAccountType = "Perpetual";
    option.searchBy = searchBy3;
  }

  if (option.exchangeId === exchangeNames.HUOBI) {
    const isUSDTM = option.accountType.toLowerCase() === "perpetuals-usdt-m";

    if (!isUSDTM) {
      option.formattedAccountType = option.accountType
        .replace(/-\w+$/i, "")
        .toLowerCase();
      option.searchBy = searchBy3;
    }
  }

  if (option.exchangeId === exchangeNames.OKEX) {
    option.formattedAccountType = option.accountType.match(/^\w+/i)[0];
    option.searchBy = searchBy3;
  }

  if (showRawMarginAccountType(option)) {
    option.formattedAccountType = "Margin-isolated";
    option.searchBy = searchBy3;
  }

  if (
    option.exchangeId === exchangeNames.BINANCE &&
    option.accountType === "portfolio_margin"
  ) {
    option.formattedAccountType = "Portfolio Margin";
    option.searchBy = searchBy3;
  }

  if (
    option.exchangeId === exchangeNames.BINANCE &&
    option.accountType === "portfolio_margin_pro"
  ) {
    option.formattedAccountType = "Portfolio Margin Pro";
    option.searchBy = searchBy3;
  }

  if (
    option.exchangeId === exchangeNames.BINANCE &&
    option.accountType === "portfolio_margin_pro_span"
  ) {
    option.formattedAccountType = "Portfolio Margin Pro SPAN";
    option.searchBy = searchBy3;
  }

  if (
    option.exchangeId === exchangeNames.BINANCE &&
    option.accountType === "cross_margin"
  ) {
    option.formattedAccountType = "Cross Margin";
    option.searchBy = searchBy3;
  }

  option.uid = `${option.apiKeyId}-${option.accountType}-nested`;
  option.total = getRoundedDownValue(option.total ?? 0, option.currency);

  return option;
}

function filterTransferOptions(options, exchangeNames) {
  const exchangesWithUnifiedAccounts = [
    exchangeNames.BITGET,
    exchangeNames.COPPER_BITGET,
    exchangeNames.KUCOIN_HF,
  ];

  const groupedOptions = options.reduce((memo, item) => {
    const key = `${item.exchangeId}:${item.apiKeyId}`;

    if (!memo[key]) {
      memo[key] = [];
    }

    memo[key].push(item);

    return memo;
  }, {});

  return Object.values(groupedOptions).flatMap((accounts) => {
    const [firstAccount] = accounts;

    if (!exchangesWithUnifiedAccounts.includes(firstAccount.exchangeId)) {
      return accounts;
    }

    const unifiedAccount = accounts.find(
      ({ accountType }) => accountType === "unified-account",
    );
    const spotAccount = accounts.find(
      ({ accountType }) => accountType === "spot",
    );
    const hasUnifiedBalance = Number(unifiedAccount?.total ?? 0) !== 0;
    const hasSpotBalance = Number(spotAccount?.total ?? 0) !== 0;

    if (!hasSpotBalance) {
      return unifiedAccount ? [unifiedAccount] : accounts;
    }

    if (hasUnifiedBalance && hasSpotBalance) {
      return [unifiedAccount, spotAccount].filter(Boolean);
    }

    return spotAccount ? [spotAccount] : accounts;
  });
}

export default function (
  currency,
  from = null,
  filterAccountsOptions = () => {},
) {
  if (!currency) {
    return [];
  }

  const store = require("@/store/store");
  const getters = store.default.getters;

  const state = store.default.state;
  const { derivativesCollateral } = state;
  const { balance, exchangeNames, getExchangeFullName, getAliasCurrency } =
    getters;
  const gateUnifiedAccountsSet = new Set(state.apiKeys.gateUnifiedAccounts);

  let accountOptions = balance.reduce((memo, item) => {
    if (
      gateUnifiedAccountsSet.has(item.apiKeyId) ||
      [exchangeNames.CRYPTOCOM, exchangeNames.HYPERLIQUID].includes(
        item.exchangeId,
      )
    ) {
      return memo;
    }

    let total = item.balance[currency];

    if (USDT_CURRENCIES_SET.has(currency)) {
      total = item.balance.USDT ?? total;
    }

    memo.push({
      ...item,
      currency,
      accountType: "spot",
      total: Number(total) || 0,
    });

    return memo;
  }, []);

  const hitbtcMarginEmptyAccounts = getMarginEmptyAccounts(
    accountOptions,
    currency,
    exchangeNames.HITBTC,
  );

  const bequantMarginEmptyAccounts = getMarginEmptyAccounts(
    accountOptions,
    currency,
    exchangeNames.BEQUANT,
  );

  const getApiKey = getters["portfolio/getApiKey"];

  const collateral = derivativesCollateral
    .filter(({ apiKeyId, accountType }) => {
      if (
        gateUnifiedAccountsSet.has(apiKeyId) &&
        ["option", "perpetual-usdt-m"].includes(accountType)
      ) {
        return false;
      }

      return true;
    })
    .concat(hitbtcMarginEmptyAccounts)
    .concat(bequantMarginEmptyAccounts)
    .map((item, index) => {
      const { exchangeId, currency, accountType, apiKeyId } = item;
      const exchangeFullName = getExchangeFullName(exchangeId);
      const total = item.balance ? parseFloat(item.balance) : 0;
      const { name: apiKeyName } = getApiKey(item.apiKeyId) ?? {};
      const formattedCurrency = getAliasCurrency(exchangeId, currency);

      return {
        ...item,
        exchangeId,
        apiKeyId,
        accountType,
        apiKeyName,
        currency: formattedCurrency,
        total,
        exchangeFullName,
        label: index,
      };
    });

  accountOptions = accountOptions.concat(collateral);

  const bitmexData = collateral
    .filter((item) => item.exchangeId === exchangeNames.BITMEX)
    .map(mapXBTToBTC);

  let isBitmexExist = bitmexData.length >= 1;

  if (from) {
    isBitmexExist =
      bitmexData.filter((item) => filterAccountsOptions(item, "from")).length >=
        1 || from?.exchangeId === exchangeNames.BITMEX;
  }

  accountOptions = accountOptions
    .map(mapXBTToBTC)
    .concat(!isBitmexExist ? [] : getBitmexApiKeys(currency, collateral))
    .concat(getBitfinexMarginApiKeys(currency))
    .concat(getBitfinexPerpetualApiKeys(currency))
    .concat(getGateEmptyApiKeys(currency));

  accountOptions = handleUnifiedAccounts(accountOptions, currency);
  accountOptions = handleBinanceMarginProAccounts(accountOptions, currency);

  accountOptions = accountOptions.filter((item) => {
    let formattedCurrency = currency;

    if (item.accountType !== "spot") {
      formattedCurrency = USDT_CURRENCIES_SET.has(formattedCurrency)
        ? "USDT"
        : formattedCurrency;
    }

    return (
      item.currency === formattedCurrency &&
      item.exchangeId !== exchangeNames.FTX
    );
  });

  accountOptions = accountOptions.filter(
    (item) => !isUnavailableBinanceTransferAccount(item, from),
  );

  if (from) {
    accountOptions = accountOptions.filter((item) =>
      filterAccountsOptions(item, "from"),
    );
  }

  accountOptions = filterTransferOptions(accountOptions, exchangeNames)
    .map((item, index) => {
      return {
        ...item,
        currency:
          USDT_CURRENCIES_SET.has(item.currency) &&
          ![exchangeNames.BINANCE_CUSTODY, exchangeNames.FIREBLOCKS].includes(
            item.exchangeId,
          )
            ? "USDT"
            : item.currency,
        label: index,
      };
    })
    .sort(
      (
        { apiKeyName: apiKeyName1, exchangeId: exchangeId1 },
        { apiKeyName: apiKeyName2, exchangeId: exchangeId2 },
      ) => {
        const key1 = `${exchangeId1}:${apiKeyName1}`;
        const key2 = `${exchangeId2}:${apiKeyName2}`;

        return key1.localeCompare(key2);
      },
    );

  return getGroupedOptions(accountOptions);
}
